/**
 * Copyright (c) Mayoral Moda Infantil, S.A.U. and affiliates.
 */

import SidebarSeasonSelector from '@components/ui/Sidebar/SidebarSeasonSelector';
import { Stack, StackProps, styled, Typography } from '@mui/material';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import { FC, useContext, useEffect, useState } from 'react';

import SidebarContext from './SidebarContext';
import SidebarNavSkeleton from './SidebarNavSkeleton';
import SidebarSection from './SidebarSection';
import { findSelectedSection } from './sidebar.utils';

import { NavigationSection } from '@/types/app-config';

const SidebarNavBody = styled(Stack, {
  name: 'SidebarNav',
  slot: 'Body',
  shouldForwardProp: (prop) => prop !== 'isSelected',
})<{ isSelected: boolean }>(({ theme, isSelected }) => ({
  position: 'relative',
  transition: theme.transitions.create('transform', {
    duration: theme.transitions.duration.short,
    easing: theme.transitions.easing.easeInOut,
  }),
  transform: 'translateX(0)',
  ...(isSelected && {
    transform: 'translateX(calc(-100% - 10px))',
  }),
}));

/**
 * Sidebar Brand
 */
const SidebarNav: FC<StackProps> = (props) => {
  const { pathname } = useRouter();
  const { navigation, loading } = useContext(SidebarContext);
  const [selected, setSelected] = useState<NavigationSection>(null);
  const { t } = useTranslation();

  useEffect(() => {
    const section = findSelectedSection(pathname, navigation);
    if (!section) {
      return setSelected(null);
    }

    setSelected(section);
  }, [pathname, loading]);

  return (
    <Stack direction="column" spacing={2} sx={{ px: 2 }} {...props}>
      <Stack direction="column" spacing={0.75}>
        <Typography variant="caption" sx={{ px: 1.3, color: 'dark.400', userSelect: 'none' }}>
          {t('TEMPORADA')}
        </Typography>
        <SidebarSeasonSelector />
      </Stack>

      <Stack direction="column" spacing={0.75}>
        <Typography variant="caption" sx={{ px: 1.3, color: 'dark.400', userSelect: 'none' }}>
          {t('MENU')}
        </Typography>

        <SidebarNavBody direction="column" spacing={0.5} isSelected={!!selected}>
          {loading ? (
            <SidebarNavSkeleton />
          ) : (
            navigation.map((section: NavigationSection) => {
              return (
                <SidebarSection
                  key={section.id}
                  section={section}
                  selected={selected}
                  setSelected={setSelected}
                />
              );
            })
          )}
        </SidebarNavBody>
      </Stack>
    </Stack>
  );
};

export default SidebarNav;
